import { InputWrapper } from "../../StyleField/StyleFields";
import { StyledTextField } from "../../StyleField/StyleFields";
import { FieldContainer, FieldLabel } from "../../StyleField/StyleFields";
import {
  Stack,
  Button,
  useTheme,
  useMediaQuery,
  IconButton,
  Box,
} from "@mui/material";
import React from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { SearchTagSelector } from "../../Dashboard/Talent/Card/Card_Creation/SearchTagSelector_updated";
import { ErrorText } from "../../StyleField/StyleFields";

type ProfessionalInfoFormProps = {
  values: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
  errors: any;
  setFieldValue: (field: string, value: any) => void;
  isSubmitting: boolean;
  touched: any;
};

const ProfessionalInfoForm = ({
  values,
  handleChange,
  errors,
  setFieldValue,
  isSubmitting,
  touched,
}: ProfessionalInfoFormProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handlePortfolioChange = (index: number, value: string) => {
    const updatedLinks = [...(values.portfolio_links || [])];
    updatedLinks[index] = value;
    setFieldValue("portfolio_links", updatedLinks);
  };

  const addPortfolioLink = () => {
    const updatedLinks = [...(values.portfolio_links || []), ""];
    setFieldValue("portfolio_links", updatedLinks);
  };

  const removePortfolioLink = (index: number) => {
    const updatedLinks = [...(values.portfolio_links || [])];
    updatedLinks.splice(index, 1);
    setFieldValue("portfolio_links", updatedLinks);
  };

  const handleSocialLinkChange = (index: number, value: string) => {
    const updatedLinks = [...(values.social_links || [])];
    const socialType = detectSocialType(value);
    updatedLinks[index] = {
      type: socialType,
      url: value,
    };
    setFieldValue("social_links", updatedLinks);
  };

  const addSocialLink = () => {
    const updatedLinks = [
      ...(values.social_links || []),
      { type: "", url: "" },
    ];
    setFieldValue("social_links", updatedLinks);
  };

  const removeSocialLink = (index: number) => {
    const updatedLinks = [...(values.social_links || [])];
    updatedLinks.splice(index, 1);
    setFieldValue("social_links", updatedLinks);
  };

  const detectSocialType = (url: string): string => {
    if (!url) return "";

    const urlLower = url.toLowerCase();
    if (urlLower.includes("facebook.com")) return "Facebook";
    if (urlLower.includes("instagram.com")) return "Instagram";
    if (urlLower.includes("tiktok.com")) return "TikTok";
    if (urlLower.includes("youtube.com")) return "YouTube";
    return "";
  };

  return (
    <div>
      {/* Portfolio Links */}
      <FieldContainer>
        <FieldLabel>Portfolio Links</FieldLabel>
        <Stack flexDirection="column" gap={1}>
          {values?.portfolio_links?.map((link: string, index: number) => (
            <InputWrapper key={index}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                width="100%"
              >
                <Box sx={{ flex: 1 }}>
                  <StyledTextField
                    name={`portfolio_links.${index}`}
                    placeholder="http://example.com"
                    value={link}
                    onChange={(e) =>
                      handlePortfolioChange(index, e.target.value)
                    }
                    error={
                      Boolean(errors.portfolio_links?.[index]) &&
                      touched.portfolio_links?.[index]
                    }
                    fullWidth
                  />
                </Box>
                <IconButton
                  onClick={() => removePortfolioLink(index)}
                  sx={{
                    color: "var(--neutral-500)",
                    "&:hover": {
                      color: "var(--neutral-700)",
                    },
                  }}
                >
                  <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Stack>
            </InputWrapper>
          ))}
        </Stack>
        <Button
          startIcon={<AddIcon />}
          onClick={addPortfolioLink}
          sx={{
            color: "var(--primary-600)",
            textTransform: "none",
            marginRight: "auto",
            width: "fit-content",
            mt: 1,
            fontSize: isMobile ? "0.875rem" : "1rem",
          }}
        >
          Add more
        </Button>
      </FieldContainer>

      {/* Social Links */}
      <FieldContainer>
        <FieldLabel>Socials</FieldLabel>
        <Stack flexDirection="column" gap={1}>
          {values?.social_links?.map(
            (link: { type: string; url: string }, index: number) => (
              <InputWrapper key={index}>
                <Stack direction="column" spacing={1} width="100%">
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    width="100%"
                  >
                    <Box sx={{ flex: 1 }}>
                      <StyledTextField
                        name={`social_links.${index}.url`}
                        placeholder="http://example.com"
                        value={link.url}
                        onChange={(e) =>
                          handleSocialLinkChange(index, e.target.value)
                        }
                        error={
                          Boolean(errors.social_links?.[index]?.url) &&
                          touched.social_links?.[index]?.url
                        }
                        fullWidth
                      />
                    </Box>
                    <IconButton
                      onClick={() => removeSocialLink(index)}
                      sx={{
                        color: "var(--neutral-500)",
                        "&:hover": {
                          color: "var(--neutral-700)",
                        },
                      }}
                    >
                      <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </Stack>
                  {errors.social_links?.[index]?.url &&
                    touched.social_links?.[index]?.url && (
                      <ErrorText>{errors.social_links?.[index]?.url}</ErrorText>
                    )}
                </Stack>
              </InputWrapper>
            )
          )}
        </Stack>
        <Button
          startIcon={<AddIcon />}
          onClick={addSocialLink}
          sx={{
            color: "var(--primary-600)",
            textTransform: "none",
            marginRight: "auto",
            width: "fit-content",
            mt: 1,
            fontSize: isMobile ? "0.875rem" : "1rem",
          }}
        >
          Add social link
        </Button>
      </FieldContainer>

      {/* Search Bar */}
      <FieldContainer>
        <FieldLabel>Skills</FieldLabel>
        <SearchTagSelector
          selectedTags={values.skills || []}
          setFieldValue={setFieldValue}
          name="skills"
        />
      </FieldContainer>

      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent="flex-end"
        gap={2}
        sx={{
          width: "100%",
          mt: isMobile ? 2 : 0,
        }}
      >
        <Button
          sx={{
            borderRadius: "8px",
            color: "var(--neutral-900)",
            background: "var(--neutral-50)",
            border: "1px solid var(--neutral-100)",
            padding: "10px 16px",
            fontSize: isMobile ? "0.875rem" : "14px",
            lineHeight: "16px",
            textTransform: "none",
            width: isMobile ? "100%" : "auto",
            fontFamily: "Lexend",
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          sx={{
            borderRadius: "8px",
            color: "var(--neutral-100)",
            background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
            padding: "10px 16px",
            fontSize: isMobile ? "0.875rem" : "14px",
            lineHeight: "16px",
            textTransform: "none",
            width: isMobile ? "100%" : "auto",
            fontFamily: "Lexend",
          }}
        >
          {isSubmitting ? "Saving..." : "Save Changes"}
        </Button>
      </Stack>
    </div>
  );
};

export default ProfessionalInfoForm;
